/**
 * 手写图标路径表(规格 C4:不引图标库,手写内联 SVG)。
 *
 * 为什么不引:图标库是「一个依赖换一堆用不上的图标」,而且它的版本漂移
 * 会以「某次升级之后某一行的图标变了」的形式出现 —— 这种变化没人会 review。
 * 这里要用的图标一共十二个,写完就固定了。
 *
 * 画法约定:`viewBox="0 0 24 24"`、`fill="none"`、`stroke="currentColor"`、
 * `stroke-width="1.7"`(见 NavIcon.vue)。所有路径按同一套约定画 ——
 * 描边粗细一致、直线为主、圆角靠 linejoin —— 十二个图标摆在一列时才不像
 * 从三个不同的库里各抓了几个。
 *
 * 每一项都是**一个** `<path>` 的 d 字符串,多段子路径用 M 连写。
 */
import type { IconName } from './types'

/** 圆的两段圆弧写法 —— 描边样式下画圆用路径而不是 <circle>,好让整张表是同一种形状 */
function dot(cx: number, cy: number, r: number): string {
  return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0`
}

/**
 * 图标名 → 路径。
 *
 * 类型写成 `satisfies Record<IconName, string>` 而不是直接标注 `Record<IconName, string>`:
 * 后者是一个带索引签名的类型,在 `noUncheckedIndexedAccess` 下每次取值都会得到
 * `string | undefined`,于是组件里必须写一个 `?? ''` 的兜底 —— 而那个兜底会把
 * 「少了一个图标」变成「此处什么都不画」,页面上只是少一块,没人报错。
 * `satisfies` 保证每个 IconName 都有值(少一个当场 typecheck 报红),同时保住字面量类型,
 * 这样 NavIcon 里取值是 `string`,不需要任何兜底。
 */
export const ICON_PATHS = {
  /** 订单:一张带折角的单据 */
  orders: `M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h4`,

  /** 计价:一枚价签 */
  pricing: `M20 4h-7.5L3.8 12.7l7.5 7.5L20 11.5z${dot(16.4, 7.6, 0.9)}`,

  /** 配置:两根滑杆,每根一个旋钮 */
  config: `M4 8h16M4 16h16${dot(9, 8, 2.2)}${dot(15, 16, 2.2)}`,

  /** 账号:一个人形剪影 */
  accounts: `${dot(12, 8, 3.6)}M5 20a7 7 0 0114 0`,

  /** 承运商:一辆货车 */
  carriers: `M3 6h11v10H3zM14 9h4l3 3.5V16h-7z${dot(7.5, 18.5, 1.8)}${dot(17.5, 18.5, 1.8)}`,

  /** 数据大屏:四块拼在一起的画面 */
  dashboard: `M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z`,

  /** 列表:三行,每行一个前导点 */
  list: `M8 6h12M8 12h12M8 18h12${dot(4.6, 6, 1)}${dot(4.6, 12, 1)}${dot(4.6, 18, 1)}`,

  /** 退出:一扇门与一支离去的箭头 */
  logout: `M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4M9 15l-4-3 4-3M5 12h10`,

  /** 刷新:一段开口的圆弧加箭头 */
  refresh: `M23 4v6h-6M20.49 15a9 9 0 11-2.12-9.36L23 10`,

  /** 说明:圆圈加一个小写 i */
  info: `${dot(12, 12, 9)}M12 16.5V11${dot(12, 8, 0.95)}`,

  /** 返回:向左的箭头 */
  back: `M19 12H5M11 6l-6 6 6 6`,

  /** 外链:一个方框与一支指向框外的箭头 */
  external: `M14 4h6v6M20 4l-8 8M18 14.5V19a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h4.5`,

  /**
   * 纸飞机:品牌方块里的那一只(S-41,v2 画板 O1/O8/O9 的 brand mark)。
   * 画板量到的是「纸飞机」而不是四旋翼 —— 两端侧栏与登录页品牌区全部换用它,
   * BrandMark(四旋翼线稿)从此只作为规格 §4.3 里那个可替换的图形件存在。
   */
  plane: `M21 3 3 10.2l6.8 3 3 6.8L21 3zM9.8 13.2 21 3`,

  /** 圈内对勾:登录页品牌区的要点行、以及「已核对」这类语义(S-41) */
  check: `${dot(12, 12, 9)}M8.2 12.4l2.6 2.6 5-5.6`,

  /** 钱包:结算与对账那一格(S-43,画板 O2 侧栏的 wallet) */
  wallet: `M3 7h18v12H3zM3 10.5h18${dot(16.5, 14.5, 1)}`,

  /**
   * 审计日志那一格(S-44,画板 O3 侧栏画的是 lucide 的 file-text)。
   * 三行流水 + 一枚放大镜:这一页的动作是「在流水里查一件事」——
   * 照抄一只文件图标会让它与相邻的「订单」(同样是带折角的单据)在侧栏里撞脸。
   */
  audit: `M3.5 5h11M3.5 9h11M3.5 13h6${dot(16, 15.5, 4.2)}M19.1 18.6 21.5 21`,

  /**
   * 开户(S-45,画板 O7 那颗主按钮上画的是 lucide 的 user-plus):
   * 一个人加一个加号 —— 「再开一个账号」正是那颗按钮的动作
   * (与「accounts」那只纯人形分开:那是导航里的账号页,这是开户这个动作)。
   */
  userPlus: `M17.5 21v-1.6a4.2 4.2 0 0 0-4.2-4.2H6.7a4.2 4.2 0 0 0-4.2 4.2V21${dot(10, 7, 3.8)}M19.5 7.5v6M16.5 10.5h6`,

  /**
   * 综合监控(S-50,监管端侧栏第三项):一块屏 + 屏上一条脉冲。
   * 与「dashboard」那张四格拼板分开 —— 两项挨在同一列里,撞脸会让它们看起来是同一页。
   */
  monitor: `M3.5 4.5h17v11.5h-17zM12 16v3.5M8.5 19.5h7M6.5 11l2.5-3 2.5 4.5 2.5-5 1.5 2.5h2`,

  /**
   * 船(S-50 从 DashboardView 的内联 SVG 收进来的 —— 它当时画了两只:船壳 + 桅杆)。
   * 两块大屏(G1 的 KPI 卡 / G5 的船舶列表与地图船位)共用这一只,
   * 按单点定义纪律收进本表;合并子路径用 M 连写,形状与原来逐点相同。
   */
  ship: `M4 15.5h16l-1.7 4a1 1 0 0 1-.9.5H6.6a1 1 0 0 1-.9-.5l-1.7-4zM12 15.5V5.5M7.5 8.5h9`,

  /** 锚(S-50 同 ship:G1 的内联副本一并收进来)。三段子路径:环 + 干与横杆 + 两舷弯钩 */
  anchor: `${dot(12, 5, 2.1)}M12 7.2V20M8 10.5h8M4.5 13.5c0 4 3.4 7 7.5 7s7.5-3 7.5-7`,

  /** 放大镜(S-50,G5 轨迹面板的搜索框;描边风格与全表一致) */
  search: `${dot(11, 11, 6.2)}M15.4 15.4 20.5 20.5`,

  /** 凭证夹(S-50 从 DashboardView 收进来:G1 第三张 KPI 卡「今日外轮代理单」的那只) */
  clipboard: `M9 4h6M8 4h8a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM9.5 10.5h5M9.5 14h3.5`,
} satisfies Record<IconName, string>
